import { useCallback } from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { useAuth } from "@/hooks/use-auth";
import { isAuthRequiredPath } from "@/components/auth/auth-required-routes";

/**
 * `useNavigate`, except it will not walk a signed-out visitor into a page that
 * needs an account.
 *
 * Sending them there and *then* showing the gate is a worse trade than it
 * looks: the URL says they arrived, the page renders inert and half-loaded
 * behind the modal, and if they dismiss it they are stranded somewhere they
 * cannot use. Asking first keeps them on the page they were reading, and the
 * navigation still happens — after they are in.
 *
 * Reach for this instead of `useNavigate` for any destination listed in
 * `auth-required-routes.ts`. Public destinations pass straight through, so it
 * is safe to use on a mix (the sidebar's rows are a mix).
 *
 * Dismissing does nothing at all, which is the point: the caller stays where
 * they are and no navigation was attempted, so there is nothing to undo.
 */
export const useGuardedNavigate = () => {
  const navigate = useNavigate();
  const { requireAuth } = useAuthRequired();
  const { data: isAuthenticated } = useAuth();
  const { t } = useTranslation();

  return useCallback(
    async (to: string, options?: NavigateOptions): Promise<void> => {
      if (isAuthenticated || !isAuthRequiredPath(to)) {
        navigate(to, options);
        return;
      }

      /*
       * The gate, not the sign-in form: someone clicking "My Topics" has not
       * said they want to sign in, and most visitors behind this have no
       * account yet. The reason screen is what offers them one.
       */
      const authed = await requireAuth({
        title: t("auth.modal.required.title", "Sign in to continue"),
        description: t(
          "auth.modal.required.guardDescription",
          "This page is saved to your account. Sign in or create one to open it.",
        ),
      });

      if (authed) navigate(to, options);
    },
    [isAuthenticated, navigate, requireAuth, t],
  );
};
